<template>
    <div ref="rootEl" class="flex flex-col gap-6">
        <header>
            <h2 v-if="currentPage?.topSection" class="body-bold text-valence-grey-850 leading-tight mb-1">{{ currentPage.topSection.title }}</h2>
            <p v-if="currentPage?.topSection?.instructions" class="body-small text-valence-grey-750 leading-relaxed mb-3">
                {{ currentPage.topSection.instructions }}
            </p>
            <div class="flex items-center justify-between details font-medium text-valence-grey-750 mb-3">
                <span>{{ progressLabel }}</span>
            </div>
            <BaseProgressBar :value="progressPct" size="sm" :aria-label="`Progress: ${progressPct}%`" :color="accentColor" />
        </header>

        <div v-for="item in pageItems" :key="item.question.id" class="flex flex-col gap-3">
            <div v-if="item.subheading" class="details font-semibold uppercase tracking-wider text-valence-primary-500">{{ item.subheading }}</div>
            <QuestionCard
                :question="item.question"
                :score="item.question.saved.score"
                :text="textDrafts[item.question.id]"
                :not-observed="item.question.saved.not_observed"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :show-unanswered="showValidation && isRequiredUnanswered(item.question)"
                :accent-color="accentColor"
                @update:score="setScore(item.question, $event)"
                @update:text="textDrafts[item.question.id] = $event"
                @update:not-observed="setNotObserved(item.question, $event)"
                @update:busy="setBusy(item.question.id, $event)" />
        </div>

        <BaseNotification v-if="error" theme="warning" :text="error" hide-dismiss />

        <div class="flex items-center justify-between">
            <button
                :disabled="saving || composerBusy"
                class="body-small text-valence-grey-750 hover:text-valence-grey-850 disabled:opacity-30 flex items-center gap-1"
                @click="goPrev">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
            </button>
            <BaseButton v-if="!isLastPage" theme="primary" :disabled="saving || composerBusy" class="inline-flex items-center gap-1" @click="goNext">
                Next <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton v-else theme="primary" :disabled="submitting || saving || composerBusy" @click="onSubmit">
                {{ submitting ? "Submitting…" : "Submit" }}
            </BaseButton>
        </div>
        <div v-if="composerBusy" class="details text-valence-grey-750 text-right -mt-3">Stop recording to continue.</div>

        <ConfirmationDialog
            v-model:open="showSubmitConfirm"
            title="Submit your responses?"
            confirm-label="Submit responses"
            cancel-label="Keep editing"
            :confirm-disabled="submitting"
            :close-on-confirm="false"
            @confirm="confirmSubmit">
            <template #content>
                <div class="body text-valence-grey-750">Once you submit, <strong class="text-valence-grey-850">your responses are final and can't be changed.</strong></div>
            </template>
        </ConfirmationDialog>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { QuestionWithSaved, SectionSummary } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import { buildPageItems, buildSectionPages, type SectionPage, sectionsByCode as toSectionsByCode } from "~vue/utils/assessmentSections";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { computed, reactive, ref } from "vue";

const props = defineProps<{
    responseId: string;
    questions: QuestionWithSaved[];
    sections: SectionSummary[];
    // LAB-798: forwarded to each QuestionCard to hide the score-example composer.
    allowScoreExamples?: boolean;
    // Forwarded to each QuestionCard to hide the "Not observed" tile.
    allowNotObserved: boolean;
    // Present only for unauthenticated external raters — appended to the
    // response-scoped calls so the backend can authorize via the invite token.
    inviteToken?: string;
    // Raw hex accent for the progress bar and score tiles, matching the host
    // flow's reflection family color.
    accentColor?: string;
}>();

// External raters carry no JWT; the invite token is their entitlement on the
// response-scoped endpoints. Authenticated callers (self-assessment, internal
// raters) omit it and authorize via their session/JWT.
const inviteTokenQuery = computed(() => (props.inviteToken ? `?invite_token=${props.inviteToken}` : ""));

const emit = defineEmits<{
    // Fired after a successful submit. The host owns the redirect + analytics;
    // it reads the (mutated-in-place) question list it passed in for counts.
    submitted: [];
    // Fired when the rater clicks "Back" from the first section page.
    exit: [];
}>();

const rootEl = ref<HTMLElement | null>(null);
const currentPageIndex = ref(0);
const error = ref<string | null>(null);
const saving = ref(false);
const submitting = ref(false);
const showSubmitConfirm = ref(false);

// Per-question text drafts, persisted-answer snapshots, and recording state.
// Props are populated at creation time (host renders this only after the
// payload loads), so we can seed synchronously.
const textDrafts = reactive<Record<string, string>>({});
const persisted = new Map<string, string>();
const busyIds = ref<Set<string>>(new Set());

function snapshot(q: QuestionWithSaved, text: string): string {
    return JSON.stringify({ score: q.saved.score, text, not_observed: q.saved.not_observed });
}

for (const q of props.questions) {
    textDrafts[q.id] = q.saved.text ?? "";
    persisted.set(q.id, snapshot(q, q.saved.text ?? ""));
}

/** Re-seed drafts and snapshots for answers that were written server-side. */
function adoptSavedAnswers(questionIds: string[]) {
    for (const q of props.questions.filter((question) => questionIds.includes(question.id))) {
        const text = q.saved.text ?? "";
        textDrafts[q.id] = text;
        persisted.set(q.id, snapshot(q, text));
    }
}

/** Jump to the final section page, so a filled assessment lands on Submit. */
function goToLastPage() {
    currentPageIndex.value = pages.value.length - 1;
    scrollToTop();
}

defineExpose({ adoptSavedAnswers, goToLastPage });

const sectionsByCode = computed(() => toSectionsByCode(props.sections));

const pages = computed<SectionPage<QuestionWithSaved>[]>(() => buildSectionPages(props.questions, sectionsByCode.value));

const currentPage = computed<SectionPage<QuestionWithSaved> | null>(() => pages.value[currentPageIndex.value] ?? null);
const isLastPage = computed(() => currentPageIndex.value === pages.value.length - 1);
const progressPct = computed(() => {
    if (pages.value.length === 0) {
        return 0;
    }
    return Math.round(((currentPageIndex.value + 1) / pages.value.length) * 100);
});
const progressLabel = computed(() => `Section ${currentPageIndex.value + 1} of ${pages.value.length}`);

const pageItems = computed(() => buildPageItems(currentPage.value?.questions ?? [], sectionsByCode.value));

const composerBusy = computed(() => busyIds.value.size > 0);

function setBusy(id: string, busy: boolean) {
    const next = new Set(busyIds.value);
    if (busy) {
        next.add(id);
    } else {
        next.delete(id);
    }
    busyIds.value = next;
}

function setScore(q: QuestionWithSaved, n: number) {
    q.saved = { ...q.saved, score: n, not_observed: false };
}

function setNotObserved(q: QuestionWithSaved, v: boolean) {
    q.saved = { ...q.saved, score: v ? null : q.saved.score, not_observed: v };
}

function isDirty(q: QuestionWithSaved): boolean {
    return snapshot(q, textDrafts[q.id] ?? "") !== persisted.get(q.id);
}

async function saveQuestion(q: QuestionWithSaved): Promise<boolean> {
    const text = textDrafts[q.id] ?? "";
    const { error: fetchError } = await useFetch(`/v1/reflections/responses/${props.responseId}/answers${inviteTokenQuery.value}`)
        .post(
            JSON.stringify({
                question_id: q.id,
                score: q.saved.score,
                text,
                not_observed: q.saved.not_observed,
            }),
            "application/json",
        )
        .json();
    if (fetchError.value) {
        return false;
    }
    q.saved = { ...q.saved, text };
    persisted.set(q.id, snapshot(q, text));
    return true;
}

// Persist every answer changed on the current page. Mirrors the one-per-page
// renderer's save-on-navigate model, batched across the page's questions.
async function saveDirtyOnPage(): Promise<boolean> {
    const dirty = (currentPage.value?.questions ?? []).filter(isDirty);
    if (dirty.length === 0) {
        return true;
    }
    saving.value = true;
    error.value = null;
    try {
        const results = await Promise.all(dirty.map(saveQuestion));
        if (results.some((ok) => !ok)) {
            error.value = "Could not save your answers. Please try again.";
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

// Reset to the top on section change. The sidebar layout (self-assess) scrolls
// inside an Inertia [scroll-region] container; the rater flow scrolls the
// window — reset both so it works in either host.
function scrollToTop() {
    rootEl.value?.closest("[scroll-region]")?.scrollTo({ top: 0 });
    globalThis.scrollTo({ top: 0 });
}

// Sync the page's text drafts into each question's saved state so validation
// reflects what the user has typed (drafts are otherwise only persisted on save).
function syncDraftsOnCurrentPage() {
    for (const q of currentPage.value?.questions ?? []) {
        const draft = textDrafts[q.id] ?? "";
        if (draft !== q.saved.text) {
            q.saved = { ...q.saved, text: draft };
        }
    }
}

function unansweredOnCurrentPage(): QuestionWithSaved[] {
    return (currentPage.value?.questions ?? []).filter(isRequiredUnanswered);
}

/** Sync drafts and validate required questions on the current page. Returns true if valid. */
function validateCurrentPage(action: "continuing" | "submitting"): boolean {
    syncDraftsOnCurrentPage();
    const unanswered = unansweredOnCurrentPage();
    if (unanswered.length > 0) {
        showValidation.value = true;
        error.value =
            unanswered.length === 1
                ? `Please answer the required question on this page before ${action}.`
                : `Please answer all required questions on this page before ${action}. ${unanswered.length} are still unanswered.`;
        return false;
    }
    showValidation.value = false;
    error.value = null;
    return true;
}

async function goNext() {
    if (isLastPage.value) {
        return;
    }
    if (!validateCurrentPage("continuing")) {
        return;
    }
    if (!(await saveDirtyOnPage())) {
        return;
    }
    currentPageIndex.value += 1;
    scrollToTop();
}

async function goPrev() {
    if (currentPageIndex.value === 0) {
        if (!(await saveDirtyOnPage())) {
            return;
        }
        emit("exit");
        return;
    }
    if (!(await saveDirtyOnPage())) {
        return;
    }
    currentPageIndex.value -= 1;
    scrollToTop();
}

const showValidation = ref(false);

async function onSubmit() {
    if (submitting.value) {
        return;
    }
    if (!validateCurrentPage("submitting")) {
        return;
    }
    if (!(await saveDirtyOnPage())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function confirmSubmit() {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    try {
        const { error: fetchError, data } = await useFetch(`/v1/reflections/responses/${props.responseId}/submit${inviteTokenQuery.value}`).post().json();
        if (fetchError.value) {
            error.value = (data.value as { error?: string })?.error || "Could not submit.";
            showSubmitConfirm.value = false;
            return;
        }
        emit("submitted");
    } finally {
        submitting.value = false;
    }
}
</script>
